import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FaPercentage,
  FaUsers,
  FaMoneyBillWave,
  FaChartLine,
  FaEdit,
  FaCheck,
  FaClock,
  FaInfoCircle,
  FaChartBar,
  FaEye,
  FaTimes,
  FaDownload
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import { API_URL } from '../../config/apiConfig';
import { getVendorCommissionSettings } from '../../features/vendor/vendorWalletSlice';

const VendorCommissionPanel = ({ vendorId, vendorData, isAdminView = false }) => {
  const dispatch = useDispatch();
  const reduxCommissionData = useSelector((state) => state.vendorWallet.commissionSettings);
  const loadingCommission = useSelector((state) => state.vendorWallet.loadingCommission);

  const [commissionData, setCommissionData] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showEarningsModal, setShowEarningsModal] = useState(false);
  const [showBenchmarkModal, setShowBenchmarkModal] = useState(false);
  
  // Form state for setting commission rate
  const [rateForm, setRateForm] = useState({
    percentage: '',
    description: ''
  });
  const [settingRate, setSettingRate] = useState(false);

  const displayCommissionData = isAdminView ? commissionData : reduxCommissionData;

  useEffect(() => {
    if (vendorId) {
      if (isAdminView) {
        const fetchAdminData = async () => {
          setLoading(true);
          try {
            await fetchCommissionData();
            await fetchEarningsData();
            await fetchBenchmarkData();
          } catch (error) {
            console.error("Admin fetch error:", error);
          } finally {
            setLoading(false);
          }
        };
        fetchAdminData();
      } else {
        const fetchVendorData = async () => {
          setLoading(true);
          try {
            let token = localStorage.getItem('vendorToken');
            const earningsUrl = `${API_URL}/vendor/${vendorId}/earnings-report`;
            const benchmarkUrl = `${API_URL}/vendor/${vendorId}/commission/benchmark`;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            
            await Promise.all([
              dispatch(getVendorCommissionSettings(vendorId)).unwrap(),
              axios.get(earningsUrl, config).then(res => {
                if (res.data.status || res.data.success) setEarnings(res.data.data);
              }),
              axios.get(benchmarkUrl, config).then(res => {
                if (res.data.status || res.data.success) setBenchmark(res.data.data);
              })
            ]);
          } catch (error) {
            console.error("Vendor parallel fetch error:", error);
          } finally {
            setLoading(false);
          }
        };
        fetchVendorData();
      }
    }
  }, [vendorId, isAdminView, dispatch]);

  const handleRetry = () => {
    if (isAdminView) {
      fetchCommissionData();
      fetchEarningsData();
      fetchBenchmarkData();
    } else {
      setLoading(true);
      let token = localStorage.getItem('vendorToken');
      const earningsUrl = `${API_URL}/vendor/${vendorId}/earnings-report`;
      const benchmarkUrl = `${API_URL}/vendor/${vendorId}/commission/benchmark`;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      Promise.all([
        dispatch(getVendorCommissionSettings(vendorId)).unwrap(),
        axios.get(earningsUrl, config).then(res => {
          if (res.data.status || res.data.success) setEarnings(res.data.data);
        }),
        axios.get(benchmarkUrl, config).then(res => {
          if (res.data.status || res.data.success) setBenchmark(res.data.data);
        })
      ]).catch(error => {
        console.error("Vendor parallel fetch error on retry:", error);
      }).finally(() => {
        setLoading(false);
      });
    }
  };

  const fetchCommissionData = async () => {
    try {
      let token, url;

      if (isAdminView) {
        const userInfo = JSON.parse(localStorage.getItem("user"));
        token = userInfo?.token;
        url = `${API_URL}/commission/admin/vendor/${vendorId}/settings`;
      } else {
        token = localStorage.getItem('vendorToken');
        url = `${API_URL}/vendor/${vendorId}/commission/settings`;
      }


      if (!vendorId) {
        console.error('❌ No vendorId provided to VendorCommissionPanel');
        toast.error('Vendor ID is missing');
        return;
      }

      if (!token) {
        console.error('❌ No authentication token found');
        toast.error('Authentication token missing. Please login again.');
        return;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });


      if (response.data.success || response.data.status) {
        setCommissionData(response.data.data);
      } else {
        toast.error('Commission data format issue');
      }
    } catch (error) {
      console.error('❌ Error fetching commission data:', {
        error: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        vendorId,
        responseData: error.response?.data,
        requestHeaders: error.config?.headers,
        networkError: !error.response && error.code
      });


      if (error.response?.status === 404) {
        console.error('❌ 404 Error Details:', {
          fullUrl: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          url: error.config?.url
        });
        toast.error(`Endpoint not found: ${error.config?.url}`);
      } else if (error.response?.status === 401) {
        console.error('❌ 401 Auth Error:', error.response?.data);
        toast.error('Authentication failed. Please login again.');
      } else {
        console.error('❌ Other Error:', error.response?.data || error.message);
        toast.error(`Error: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  const fetchEarningsData = async () => {
    try {
      let token, url;

      if (isAdminView) {
        const userInfo = JSON.parse(localStorage.getItem("user"));
        token = userInfo?.token;
        url = `${API_URL}/commission/admin/vendor/${vendorId}/earnings`;
      } else {
        token = localStorage.getItem('vendorToken');
        url = `${API_URL}/vendor/${vendorId}/earnings-report`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status || response.data.success) {
        setEarnings(response.data.data);
      }
    } catch (error) {
      console.error('❌ Error fetching earnings data:', {
        error: error.message,
        status: error.response?.status,
        url: error.config?.url,
        vendorId,
        responseData: error.response?.data
      });

      if (error.response?.status === 404) {
        toast.error(`Earnings endpoint not found: ${error.config?.url}`);
      }
    }
  };

  const fetchBenchmarkData = async () => {
    try {
      let token, url;

      if (isAdminView) {
        const userInfo = JSON.parse(localStorage.getItem("user"));
        token = userInfo?.token;
        url = `${API_URL}/commission/admin/vendor/${vendorId}/benchmark`;
      } else {
        token = localStorage.getItem('vendorToken');
        url = `${API_URL}/vendor/${vendorId}/commission/benchmark`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status || response.data.success) {
        setBenchmark(response.data.data);
      }
    } catch (error) {
      console.error('❌ Error fetching benchmark data:', {
        error: error.message,
        status: error.response?.status,
        url: error.config?.url,
        vendorId,
        responseData: error.response?.data
      });

      if (error.response?.status === 404) {
        toast.error(`Benchmark endpoint not found: ${error.config?.url}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRateSubmit = async (e) => {
    e.preventDefault();

    if (isAdminView) {
      toast.error('Admins cannot set vendor commission rates. Vendors manage their own rates.');
      return;
    }

    if (!rateForm.percentage) {
      toast.error('Please enter a commission rate');
      return;
    }

    if (rateForm.percentage < 0) {
      toast.error('Commission rate cannot be negative');
      return;
    }

    setSettingRate(true);
    try {
      const token = localStorage.getItem('vendorToken');
      const response = await axios.post(
        `${API_URL}/vendor/${vendorId}/commission/set-rate`,
        rateForm,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.status || response.data.success) {
        toast.success(`Commission rate set to ${rateForm.percentage}% successfully`);
        setShowRequestModal(false);
        setRateForm({ percentage: '', description: '' });
        dispatch(getVendorCommissionSettings(vendorId)); // Refresh data in this panel
        try {
          // Notify other tabs/components
          const evt = new CustomEvent('vendor-commission-updated', { detail: { vendorId, newRate: rateForm.percentage } });
          window.dispatchEvent(evt);
          localStorage.setItem('vendorCommissionRateUpdated', JSON.stringify({ vendorId, ts: Date.now(), newRate: rateForm.percentage }));
        } catch (e) {
        }
      }
    } catch (error) {
      console.error('Error setting rate:', error);
      toast.error(error.response?.data?.message || 'Failed to set commission rate');
    } finally {
      setSettingRate(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? `${num}%` : 'N/A';
  };

  const handleDownloadReport = () => {
    if (!earnings) {
      toast.error('Earnings data not available');
      return;
    }

    try {
      const totalRides = earnings.summary.totalRides;
      const avgRide = formatCurrency(earnings.summary.avgEarningsPerRide || earnings.summary.averageCommissionPerRide || 0);
      const totalEarned = formatCurrency(earnings.summary.totalEarnings || earnings.summary.totalCommissionEarned || 0);

      const tableRows = (!earnings.recentBookings || earnings.recentBookings.length === 0) 
        ? `<tr><td colspan="6" style="text-align: center; padding: 12px; border-bottom: 1px solid #e2e8f0; color: #718096; font-size: 13px;">No recent bookings found</td></tr>`
        : earnings.recentBookings.map(booking => `
            <tr style="border-bottom: 1px solid #edf2f7;">
              <td style="padding: 10px 12px; font-size: 13px; color: #2d3748;">#${booking.bookingId}</td>
              <td style="padding: 10px 12px; font-size: 13px; color: #2d3748;">${booking.driverName}</td>
              <td style="padding: 10px 12px; font-size: 12px; color: #4a5568;">${booking.route}</td>
              <td style="padding: 10px 12px; font-size: 13px; font-weight: 500; text-align: right; color: #2d3748;">${formatCurrency(booking.totalAmount)}</td>
              <td style="padding: 10px 12px; font-size: 13px; font-weight: 600; text-align: right; color: #38a169;">${formatCurrency(booking.vendorCommission)}</td>
              <td style="padding: 10px 12px; font-size: 12px; color: #718096;">${new Date(booking.completedAt).toLocaleDateString()}</td>
            </tr>
          `).join('');

      const reportHTML = `
        <div style="padding: 30px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #2d3748; max-width: 800px; margin: 0 auto; background-color: #ffffff;">
          <!-- Header -->
          <div style="border-bottom: 2px solid #805ad5; padding-bottom: 15px; margin-bottom: 25px; display: flex; justify-content: space-between; align-items: flex-end;">
            <div>
              <h1 style="margin: 0; font-size: 26px; color: #4a154b; font-weight: 800; letter-spacing: -0.5px;">CabZigo</h1>
              <p style="margin: 2px 0 0 0; font-size: 14px; color: #718096; font-weight: 500;">Vendor Commission Earnings Report</p>
            </div>
            <div style="text-align: right;">
              <p style="margin: 0; font-size: 12px; color: #a0aec0; font-weight: 500;">Report Date: ${new Date().toLocaleDateString()}</p>
            </div>
          </div>

          <!-- Summary Cards -->
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 30px;">
            <div style="background-color: #ebf8ff; border: 1px solid #bee3f8; border-radius: 8px; padding: 15px; text-align: center;">
              <p style="margin: 0 0 5px 0; font-size: 12px; color: #2b6cb0; text-transform: uppercase; font-weight: 700; letter-spacing: 0.5px;">Total Rides</p>
              <p style="margin: 0; font-size: 24px; font-weight: 800; color: #2c5282;">${totalRides}</p>
            </div>
            <div style="background-color: #faf5ff; border: 1px solid #e9d8fd; border-radius: 8px; padding: 15px; text-align: center;">
              <p style="margin: 0 0 5px 0; font-size: 12px; color: #6b46c1; text-transform: uppercase; font-weight: 700; letter-spacing: 0.5px;">Avg per Ride</p>
              <p style="margin: 0; font-size: 24px; font-weight: 800; color: #553c9a;">${avgRide}</p>
            </div>
            <div style="background-color: #f0fff4; border: 1px solid #c6f6d5; border-radius: 8px; padding: 15px; text-align: center;">
              <p style="margin: 0 0 5px 0; font-size: 12px; color: #2f855a; text-transform: uppercase; font-weight: 700; letter-spacing: 0.5px;">Total Earnings</p>
              <p style="margin: 0; font-size: 24px; font-weight: 800; color: #276749;">${totalEarned}</p>
            </div>
          </div>

          <!-- Recent Bookings Table -->
          <div>
            <h2 style="margin: 0 0 15px 0; font-size: 16px; font-weight: 700; color: #1a202c; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px;">Recent Earnings Details</h2>
            <table style="width: 100%; border-collapse: collapse; text-align: left;">
              <thead>
                <tr style="background-color: #f7fafc; border-bottom: 1px solid #cbd5e0;">
                  <th style="padding: 10px 12px; font-size: 12px; font-weight: 700; color: #4a5568; text-transform: uppercase; width: 12%;">Booking</th>
                  <th style="padding: 10px 12px; font-size: 12px; font-weight: 700; color: #4a5568; text-transform: uppercase; width: 22%;">Driver</th>
                  <th style="padding: 10px 12px; font-size: 12px; font-weight: 700; color: #4a5568; text-transform: uppercase; width: 33%;">Route</th>
                  <th style="padding: 10px 12px; font-size: 12px; font-weight: 700; color: #4a5568; text-transform: uppercase; text-align: right; width: 15%;">Fare</th>
                  <th style="padding: 10px 12px; font-size: 12px; font-weight: 700; color: #4a5568; text-transform: uppercase; text-align: right; width: 18%;">Commission</th>
                  <th style="padding: 10px 12px; font-size: 12px; font-weight: 700; color: #4a5568; text-transform: uppercase; width: 12%;">Date</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
          </div>

          <!-- Footer -->
          <div style="margin-top: 40px; border-top: 1px dashed #cbd5e0; padding-top: 15px; text-align: center;">
            <p style="margin: 0; font-size: 11px; color: #a0aec0; font-weight: 500;">Thank you for partnering with CabZigo.</p>
          </div>
        </div>
      `;

      const element = document.createElement('div');
      element.innerHTML = reportHTML;

      const opt = {
        margin: 10,
        filename: `CabZigo-CommissionReport-${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
      };

      html2pdf().set(opt).from(element).save();
      toast.success('Report downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF report:', error);
      toast.error('Failed to generate PDF report');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded mb-4 w-1/3"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="h-24 bg-gray-200 rounded"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!displayCommissionData) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="text-center text-red-600">
          <FaTimes className="mx-auto text-4xl mb-2" />
          <p className="text-lg font-semibold mb-2">Failed to load commission data</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleRetry}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaEdit className="inline mr-2" />
              Retry Loading
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { commissionSettings, commissionStructure } = displayCommissionData;
  const currentRate = commissionSettings.effectiveRate;
  const isUsingGlobal = commissionSettings.isUsingGlobal;

  return (
    <div className="space-y-6">
      {/* Current Commission Status */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-3">
              <FaPercentage className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Your Commission Rate</h3>
              <p className="text-gray-600">
                {isUsingGlobal ? 'Using global rate' : 'Custom rate set'}
              </p>
            </div>
          </div>
          <button
            onClick={() => { setRateForm({ percentage: currentRate, description: '' }); setShowRequestModal(true); }}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <FaEdit className="mr-2" />
            Set Rate
          </button>
        </div>

        {/* Commission Rate Display */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-gradient-to-br from-purple-50 to-indigo-100 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Your Commission</p>
                <p className="text-3xl font-bold text-purple-900">{formatPercentage(currentRate)}</p>
                <p className="text-xs text-purple-700">Per completed ride</p>
              </div>
              <div className="p-2 bg-purple-500 rounded-lg">
                <FaPercentage className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-100 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Admin Commission</p>
                <p className="text-3xl font-bold text-blue-900">{formatPercentage(commissionStructure.adminRate)}</p>
                <p className="text-xs text-blue-700">Platform fee</p>
              </div>
              <div className="p-2 bg-blue-500 rounded-lg">
                <FaMoneyBillWave className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Driver Gets</p>
                <p className="text-3xl font-bold text-green-900">{formatPercentage(commissionStructure.driverRate)}</p>
                <p className="text-xs text-green-700">Take-home amount</p>
              </div>
              <div className="p-2 bg-green-500 rounded-lg">
                <FaUsers className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Commission Structure Explanation */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center">
            <FaInfoCircle className="mr-2 text-blue-500" />
            How Your Commission Works
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-700">Ride Fare:</span>
                <span className="font-medium">₹100 (example)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Admin Commission ({commissionStructure.adminRate}%):</span>
                <span className="font-medium text-blue-600">-₹{commissionStructure.adminRate}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Your Commission ({currentRate}%):</span>
                <span className="font-medium text-purple-600">+₹{currentRate}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-700">Driver Receives:</span>
                <span className="font-medium text-green-600">₹{commissionStructure.driverRate}</span>
              </div>
            </div>
            <div className="bg-white rounded p-3">
              <p className="text-xs text-gray-600 leading-relaxed">
                For every ride completed by your drivers, you earn {currentRate}% commission. 
                The platform takes {commissionStructure.adminRate}% for operational costs, 
                and your driver keeps {commissionStructure.driverRate}% of the total fare.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mt-6">
          <button
            onClick={() => setShowEarningsModal(true)}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FaChartLine className="mr-2" />
            View Earnings
          </button>
        </div>

        {/* Vendors can set their own rates directly - no pending requests needed */}
      </div>

      {/* Set Commission Rate Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Set Your Commission Rate</h3>
                <button
                  onClick={() => setShowRequestModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Current Rate:</span>
                    <span className="font-medium">{formatPercentage(currentRate)}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    You can set your own commission rate (minimum 0%)
                  </div>
                </div>
              </div>

              <form onSubmit={handleRateSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Commission Rate (%) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={rateForm.percentage}
                    onChange={(e) => setRateForm({...rateForm, percentage: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={`Enter your commission rate (current: ${currentRate}%)`}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Rate must be 0% or higher. Driver will get {100 - (commissionStructure?.adminRate || 10) - (parseFloat(rateForm.percentage) || 0)}%
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={rateForm.description}
                    onChange={(e) => setRateForm({...rateForm, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows="3"
                    placeholder="Optional note about this commission rate"
                  />
                </div>

                <div className="bg-blue-50 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-blue-900 mb-1">Commission Breakdown</h4>
                  <div className="text-xs text-blue-800 space-y-1">
                    <div className="flex justify-between">
                      <span>Admin Commission:</span>
                      <span>{commissionStructure?.adminRate || 10}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Your Commission:</span>
                      <span>{rateForm.percentage || '0'}%</span>
                    </div>
                    <div className="flex justify-between border-t border-blue-200 pt-1">
                      <span>Driver Gets:</span>
                      <span>{100 - (commissionStructure?.adminRate || 10) - (parseFloat(rateForm.percentage) || 0)}%</span>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowRequestModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    disabled={settingRate}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    disabled={settingRate}
                  >
                    {settingRate ? 'Setting...' : 'Set Rate'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Earnings Modal */}
      {showEarningsModal && earnings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold text-gray-900">Commission Earnings</h3>
                  <button
                    onClick={handleDownloadReport}
                    className="flex items-center gap-1.5 px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors duration-150"
                    title="Download Report as PDF"
                  >
                    <FaDownload className="text-[10px]" />
                    <span>Download PDF</span>
                  </button>
                </div>
                <button
                  onClick={() => setShowEarningsModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600">Total Rides</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {earnings.summary.totalRides}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-600">Avg per Ride</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {formatCurrency(earnings.summary.avgEarningsPerRide || earnings.summary.averageCommissionPerRide || 0)}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600">Total Earnings</p>
                  <p className="text-2xl font-bold text-green-900">
                    {formatCurrency(earnings.summary.totalEarnings || earnings.summary.totalCommissionEarned || 0)}
                  </p>
                </div>
              </div>

              {/* Recent Bookings */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Recent Commission Earnings</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Booking</th>
                        <th className="px-3 py-2 text-left">Driver</th>
                        <th className="px-3 py-2 text-left">Route</th>
                        <th className="px-3 py-2 text-right">Fare</th>
                        <th className="px-3 py-2 text-right">Your Commission</th>
                        <th className="px-3 py-2 text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {!earnings.recentBookings || earnings.recentBookings.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4 text-gray-500 font-medium">
                            No recent bookings found
                          </td>
                        </tr>
                      ) : (
                        earnings.recentBookings.map((booking) => (
                          <tr key={booking.id || booking.bookingId}>
                            <td className="px-3 py-2">#{booking.bookingId}</td>
                            <td className="px-3 py-2">{booking.driverName}</td>
                            <td className="px-3 py-2 text-xs">{booking.route}</td>
                            <td className="px-3 py-2 text-right font-medium">
                              {formatCurrency(booking.totalAmount)}
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-green-600">
                              {formatCurrency(booking.vendorCommission)}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {new Date(booking.completedAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Benchmark Modal */}
      {showBenchmarkModal && benchmark && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Market Benchmark</h3>
                <button
                  onClick={() => setShowBenchmarkModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-600">Your Rate</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {formatPercentage(benchmark.currentVendor.rate)}
                  </p>
                  <p className="text-xs text-purple-700">
                    {benchmark.insights.percentileRank}th percentile
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600">Market Average</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatPercentage(benchmark.market.averageRate)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600">Market Median</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatPercentage(benchmark.market.medianRate)}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Market Range:</span>
                    <span>
                      {formatPercentage(benchmark.market.minRate)} - {formatPercentage(benchmark.market.maxRate)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Total Vendors:</span>
                    <span>{benchmark.market.totalVendors}</span>
                  </div>
                </div>

                <div className={`p-3 rounded-lg ${
                  benchmark.insights.isAboveAverage 
                    ? 'bg-green-50 text-green-800' 
                    : 'bg-yellow-50 text-yellow-800'
                }`}>
                  <p className="text-sm font-medium">
                    {benchmark.insights.isAboveAverage 
                      ? '✅ Your rate is above market average' 
                      : '⚠️ Your rate is below market average'
                    }
                  </p>
                  {benchmark.insights.improvementPotential > 0 && (
                    <p className="text-xs mt-1">
                      Potential improvement: up to {formatPercentage(benchmark.insights.improvementPotential)} more
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorCommissionPanel;
